import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import api from '../utils/api';
import { useWorkspace } from './WorkspaceContext';

const EntitlementsContext = createContext(null);

/**
 * What this account may use, fetched once for the whole app.
 *
 * This is a mirror of the server's decision so the UI can show a gate instead of
 * an error. It is never the decision itself — every paid endpoint checks again,
 * and a stale or edited value here buys nothing.
 */
export function useEntitlements() {
  const ctx = useContext(EntitlementsContext);
  if (!ctx) {
    throw new Error('useEntitlements must be used within an EntitlementsProvider');
  }
  return ctx;
}

const FREE = {
  personalTier: 'individual_basic',
  seatTier: null,
  effectiveTier: 'individual_basic',
  assignedTeamsPro: [],
  proSource: null,
  displayPlan: { label: 'Basic', detail: null, source: null },
  role: null,
  features: [],
  locked: [],
  restricted: false,
};

function coalesceDisplayPlan(resolved) {
  if (resolved?.displayPlan?.label) return resolved.displayPlan;
  const features = resolved?.features || [];
  const isPro = features.includes('insights_full');
  const proSource = resolved?.proSource || null;
  const personalTier = resolved?.personalTier || 'individual_basic';
  if (proSource === 'teams_licence') {
    return { label: 'Pro', detail: 'via Teams', source: 'teams_licence' };
  }
  if (proSource === 'both') {
    return { label: 'Pro', detail: 'Personal and Teams', source: 'both' };
  }
  if (isPro || personalTier === 'individual_pro' || proSource === 'individual') {
    return { label: 'Pro', detail: null, source: proSource || 'individual' };
  }
  return { label: 'Basic', detail: null, source: null };
}

export function EntitlementsProvider({ children }) {
  const { current } = useWorkspace();
  const workspaceId = current?.type === 'company' ? current.id : null;
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(Boolean(localStorage.getItem('token')));
  const [loadFailed, setLoadFailed] = useState(false);
  const loadedForRef = useRef(null);
  const snapshotRef = useRef(null);

  const load = useCallback(
    async ({ force = false } = {}) => {
      const token = localStorage.getItem('token');
      const key = `${token || 'anon'}::${workspaceId || ''}`;

      if (!token) {
        loadedForRef.current = null;
        snapshotRef.current = null;
        setData(null);
        setLoadFailed(false);
        setIsLoading(false);
        return null;
      }
      if (!force && loadedForRef.current === key) return snapshotRef.current;

      loadedForRef.current = key;
      setIsLoading(true);
      try {
        const res = await api.get('/api/entitlements/me', {
          headers: workspaceId ? { 'X-Velox-Workspace': workspaceId } : undefined,
        });
        const next = res?.data || null;
        snapshotRef.current = next;
        setLoadFailed(false);
        setData(next);
        return next;
      } catch {
        loadedForRef.current = null;
        snapshotRef.current = null;
        setLoadFailed(true);
        setData(null);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [workspaceId]
  );

  useEffect(() => {
    load();
  }, [load]);

  const resolved = data || FREE;
  const displayPlan = coalesceDisplayPlan(resolved);

  const has = useCallback(
    (feature) => Array.isArray(resolved.features) && resolved.features.includes(feature),
    [resolved.features]
  );

  /** The upgrade copy and unlocking tiers for a feature the user does not have. */
  const lockInfo = useCallback(
    (feature) => (resolved.locked || []).find((l) => l.feature === feature) || null,
    [resolved.locked]
  );

  const value = useMemo(
    () => {
      const isPro = has('insights_full');
      const launch = {
        pro: data?.launch?.pro === true,
        teams: data?.launch?.teams === true,
      };
      const planLabel = displayPlan.detail
        ? `${displayPlan.label} ${displayPlan.detail}`
        : (displayPlan.label || (isPro ? 'Pro' : 'Basic'));
      return {
        ...resolved,
        displayPlan,
        isLoading,
        loadFailed,
        has,
        lockInfo,
        refresh: () => load({ force: true }),
        isPro,
        planLabel,
        launch,
        capabilities: resolved.capabilities || {},
      };
    },
    [resolved, displayPlan, data, isLoading, loadFailed, has, lockInfo, load]
  );

  return <EntitlementsContext.Provider value={value}>{children}</EntitlementsContext.Provider>;
}

export default EntitlementsContext;
